
import React, { useState, useRef, useEffect } from 'react';
import DefaultButton from '../buttons/DefaultButton';
import TextInput from '../input/TextInput';

function MailEditor({ onSend, initialData = {}, sendButtonText = 'Verzenden' }) {
    const [to, setTo] = useState(initialData.to || '');
    const [cc, setCc] = useState(initialData.cc || '');
    const [subject, setSubject] = useState(initialData.subject || '');
    const [body, setBody] = useState(initialData.body || '');
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [fontSize, setFontSize] = useState('');

    const editorRef = useRef(null);

    // Set initial content only once
    useEffect(() => {
        if (editorRef.current && body && !editorRef.current.innerHTML) {
            editorRef.current.innerHTML = body;
        }
    }, []);

    const execCommand = (command, value = null) => {
        document.execCommand(command, false, value);
        editorRef.current.focus();
        updateToolbarState();
    };

    const updateToolbarState = () => {
        setIsBold(document.queryCommandState('bold'));
        setIsItalic(document.queryCommandState('italic'));
        setIsUnderline(document.queryCommandState('underline'));
    };

    const handleBodyChange = () => {
        if (editorRef.current) {
            setBody(editorRef.current.innerHTML);
        }
    };

    const handleSend = () => {
        const emailData = {
            to,
            cc,
            subject,
            body: editorRef.current.innerHTML
        };

        if (onSend) {
            onSend(emailData);
        }
    };
    

    const changeFontSize = (e) => {
        const size = e.target.value;
        setFontSize(size);
        if (size) {
            execCommand('fontSize', size);
        }
    };

    const changeTextColor = (e) => {
        execCommand('foreColor', e.target.value);
    };

    return (
        <div>
            <div className="space-y-4">
                {/* To Field */}
                <TextInput
                    labelName="Aan:"
                    inputName="to"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    placeholder="ontvangers@example.com"
                    type="email"
                    errorMessage="Vul een geldig mailadres in"
                />

                {/* CC Field */}
                <TextInput
                    labelName="CC:"
                    inputName="cc"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    placeholder="cc@example.com"
                    type="email"
                    errorMessage="Vul een geldig mailadres in"
                />

                {/* Subject Field */}
                <TextInput
                    labelName="Onderwerp:"
                    inputName="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Email onderwerp"
                    type="text"
                />

                {/* WYSIWYG Toolbar */}
                <div>
                    <label className="block mb-1">
                        <p className="text-gray-500">Bericht:</p>
                    </label>
                    <div className="border border-gray-300 rounded-t-md bg-gray-50 p-2 flex flex-wrap gap-2 items-center">
                        {/* Text Formatting */}
                        <button
                            type="button"
                            onClick={() => execCommand('bold')}
                            className={`px-3 py-1 rounded border ${isBold ? 'bg-blue-200 border-blue-400' : 'bg-white border-gray-300'} hover:bg-gray-100 font-bold text-sm`}
                            title="Vet"
                        >
                            B
                        </button>
                        <button
                            type="button"
                            onClick={() => execCommand('italic')}
                            className={`px-3 py-1 rounded border ${isItalic ? 'bg-blue-200 border-blue-400' : 'bg-white border-gray-300'} hover:bg-gray-100 italic text-sm`}
                            title="Cursief"
                        >
                            I
                        </button>
                        <button
                            type="button"
                            onClick={() => execCommand('underline')}
                            className={`px-3 py-1 rounded border ${isUnderline ? 'bg-blue-200 border-blue-400' : 'bg-white border-gray-300'} hover:bg-gray-100 underline text-sm`}
                            title="Onderstreept"
                        >
                            U
                        </button>

                        {/* Divider */}
                        <div className="border-l border-gray-300 h-6"></div>

                        {/* Alignment */}
                        <button
                            type="button"
                            onClick={() => execCommand('justifyLeft')}
                            className="px-3 py-1 rounded border bg-white border-gray-300 hover:bg-gray-100 text-sm"
                            title="Links uitlijnen"
                        >
                            ⇤
                        </button>
                        <button
                            type="button"
                            onClick={() => execCommand('justifyCenter')}
                            className="px-3 py-1 rounded border bg-white border-gray-300 hover:bg-gray-100 text-sm"
                            title="Centreren"
                        >
                            ⇔
                        </button>
                        <button
                            type="button"
                            onClick={() => execCommand('justifyRight')}
                            className="px-3 py-1 rounded border bg-white border-gray-300 hover:bg-gray-100 text-sm"
                            title="Rechts uitlijnen"
                        >
                            ⇥
                        </button>

                        {/* Divider */}
                        <div className="border-l border-gray-300 h-6"></div>

                        {/* Lists */}
                        <button
                            type="button"
                            onClick={() => execCommand('insertUnorderedList')}
                            className="px-3 py-1 rounded border bg-white border-gray-300 hover:bg-gray-100 text-sm"
                            title="Opsommingstekens"
                        >
                            • List
                        </button>
                        <button
                            type="button"
                            onClick={() => execCommand('insertOrderedList')}
                            className="px-3 py-1 rounded border bg-white border-gray-300 hover:bg-gray-100 text-sm"
                            title="Genummerde lijst"
                        >
                            1. List
                        </button>

                        {/* Divider */}
                        <div className="border-l border-gray-300 h-6"></div>

                        {/* Font Size - Using native select styled like SelectInput */}
                        <select
                            onChange={changeFontSize}
                            value={fontSize}
                            className="px-2 py-1 h-8 text-sm rounded border bg-white border-gray-300 hover:bg-gray-100 focus:outline-none focus:border-blue-900"
                            title="Lettergrootte"
                        >
                            <option value="">Grootte</option>
                            <option value="1">Klein</option>
                            <option value="3">Normaal</option>
                            <option value="5">Groot</option>
                            <option value="7">Heel groot</option>
                        </select>

                        {/* Text Color */}
                        <input
                            type="color"
                            onChange={changeTextColor}
                            className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
                            title="Tekstkleur"
                        />

                        {/* Divider */}
                        <div className="border-l border-gray-300 h-6"></div>

                        {/* Clear Formatting */}
                        <button
                            type="button"
                            onClick={() => execCommand('removeFormat')}
                            className="px-3 py-1 rounded border bg-white border-gray-300 hover:bg-gray-100 text-sm"
                            title="Opmaak verwijderen"
                        >
                            ✕ Format
                        </button>
                    </div>

                    {/* Editor Area */}
                    <div
                        ref={editorRef}
                        contentEditable
                        onInput={handleBodyChange}
                        onMouseUp={updateToolbarState}
                        onKeyUp={updateToolbarState}
                        className="w-full min-h-[300px] p-3 border border-t-0 border-gray-300 rounded-b-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        style={{ maxHeight: '500px', overflowY: 'auto' }}
                    />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-4">
                    <DefaultButton
                        content={sendButtonText}
                        onClick={handleSend}
                    />
                </div>
            </div>
        </div>
    );
}

export default MailEditor;